import { DateFormatPattern, DiagAutoType, Task, WeekNumbering } from '../api/types';
import { formatDate } from './dateFormat';
import { weekNumber } from './weekNumber';
import { parsePredecessorsText } from './predecessors';
import { mergeCellStyle } from './cellStyle';
import { addDaysUTC, curvedPath, diffDaysUTC, orthogonalPath, parseISODate } from './ganttGeometry';
import { escapeXml, truncateToWidth } from './svgText';
import { COLUMN_WIDTH, DAY_ROW_HEIGHT, DAY_WIDTH, MONTH_ROW_HEIGHT, NUM_COL_WIDTH, ROW_HEIGHT, TIMELINE_HEADER_HEIGHT, WEEK_ROW_HEIGHT } from './pdfLayout';

/**
 * Génère le SVG vectoriel du CONTENU (tableau + frise) d'une page d'export —
 * pas l'en-tête nom/logos, qui a sa propre petite pile de dessins dans
 * pdfExport.ts (bandeau à taille fixe, indépendant de l'échelle du contenu).
 *
 * Principe de pagination : la géométrie (barres, connecteurs) est calculée
 * une seule fois pour TOUT le planning exporté, en coordonnées globales
 * ("px" logiques, jour*DAY_WIDTH / ligne*ROW_HEIGHT) — voir computeGlobalLayout
 * / computeGlobalArrows dans pdfExport.ts. Chaque page ne fait que DÉCOUPER
 * cette géométrie globale à sa fenêtre [dayStart,dayStart+dayCount) x
 * [rowStart,rowStart+rowCount), en écrêtant manuellement les éléments à
 * cheval sur un bord plutôt que de compter sur le support SVG clipPath d'un
 * convertisseur qui "ne gère qu'un sous-ensemble limité du spec" (voir
 * svg2pdf.js) — un simple Math.max/min est plus robuste qu'une dépendance
 * fonctionnelle supplémentaire.
 */

export const BORDER = '#cbd5e1';
export const TEXT_MUTED = '#64748b';
export const TEXT_DEFAULT = '#0f172a';
export const PRIMARY = '#2563eb';
export const DANGER = '#dc2626';
export const GROUP_COLOR = '#475569';
export const TODAY_COLOR = '#dc2626';
export const FONT_STACK = 'Helvetica, Arial, sans-serif';

export interface GlobalLayoutEntry {
  task: Task;
  rowIndex: number;
  isMilestone: boolean;
  hasDates: boolean;
  y: number;
  startX: number;
  endX: number;
  barLeft: number;
  barWidth: number;
}

export interface GlobalArrow {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  critical: boolean;
}

export function computeGlobalLayout(tasks: Task[], rangeStart: Date): GlobalLayoutEntry[] {
  return tasks.map((task, rowIndex) => {
    const y = rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
    if (!task.startDate || !task.endDate) {
      return { task, rowIndex, isMilestone: false, hasDates: false, y, startX: 0, endX: 0, barLeft: 0, barWidth: 0 };
    }
    const start = parseISODate(task.startDate);
    const end = parseISODate(task.endDate);
    const isMilestone = task.startDate.slice(0, 10) === task.endDate.slice(0, 10);
    const barLeft = diffDaysUTC(rangeStart, start) * DAY_WIDTH;
    const barWidth = (diffDaysUTC(start, end) + 1) * DAY_WIDTH;
    const startX = isMilestone ? barLeft + DAY_WIDTH / 2 - 7 : barLeft;
    const endX = isMilestone ? barLeft + DAY_WIDTH / 2 + 7 : barLeft + barWidth;
    return { task, rowIndex, isMilestone, hasDates: true, y, startX, endX, barLeft, barWidth };
  });
}

export function computeGlobalArrows(layout: GlobalLayoutEntry[], connectorStyle: 'curved' | 'orthogonal'): { arrows: GlobalArrow[]; pathFor: (a: GlobalArrow) => string } {
  const layoutByNumber = new Map(layout.map((l) => [l.task.number, l]));
  const arrows: GlobalArrow[] = [];
  for (const entry of layout) {
    if (!entry.hasDates || entry.task.isGroup) continue;
    for (const dep of parsePredecessorsText(entry.task.predecessorsText)) {
      const pred = layoutByNumber.get(dep.number);
      if (!pred || !pred.hasDates) continue;
      let x1: number;
      let x2: number;
      if (dep.type === 'FD') {
        x1 = pred.endX;
        x2 = entry.startX;
      } else if (dep.type === 'FF') {
        x1 = pred.endX;
        x2 = entry.endX;
      } else if (dep.type === 'DF') {
        x1 = pred.startX;
        x2 = entry.endX;
      } else {
        x1 = pred.startX;
        x2 = entry.startX;
      }
      arrows.push({ key: `${pred.task.id}-${entry.task.id}-${dep.type}`, x1, y1: pred.y, x2, y2: entry.y, critical: pred.task.isCritical && entry.task.isCritical });
    }
  }
  const pathFor = (a: GlobalArrow) => (connectorStyle === 'orthogonal' ? orthogonalPath(a.x1, a.y1, a.x2, a.y2) : curvedPath(a.x1, a.y1, a.x2, a.y2));
  return { arrows, pathFor };
}

export function textEl(
  x: number,
  y: number,
  text: string,
  opts: { fontSize?: number; fontWeight?: string; fontStyle?: string; fill?: string; anchor?: 'start' | 'middle' | 'end'; decoration?: string; fontFamily?: string } = {},
): string {
  if (!text) return '';
  const attrs = [
    `x="${x}"`,
    `y="${y}"`,
    `font-size="${opts.fontSize ?? 11}"`,
    `font-family="${escapeXml(opts.fontFamily || FONT_STACK)}"`,
    `fill="${opts.fill ?? TEXT_DEFAULT}"`,
  ];
  if (opts.fontWeight) attrs.push(`font-weight="${opts.fontWeight}"`);
  if (opts.fontStyle) attrs.push(`font-style="${opts.fontStyle}"`);
  if (opts.anchor) attrs.push(`text-anchor="${opts.anchor}"`);
  if (opts.decoration) attrs.push(`text-decoration="${opts.decoration}"`);
  return `<text ${attrs.join(' ')}>${escapeXml(text)}</text>`;
}

export function rectEl(x: number, y: number, w: number, h: number, opts: { fill?: string; stroke?: string; strokeWidth?: number; rx?: number } = {}): string {
  if (w <= 0 || h <= 0) return '';
  const attrs = [`x="${x}"`, `y="${y}"`, `width="${w}"`, `height="${h}"`];
  attrs.push(`fill="${opts.fill ?? 'none'}"`);
  if (opts.stroke) attrs.push(`stroke="${opts.stroke}"`, `stroke-width="${opts.strokeWidth ?? 1}"`);
  if (opts.rx) attrs.push(`rx="${opts.rx}"`);
  return `<rect ${attrs.join(' ')}/>`;
}

export function lineEl(x1: number, y1: number, x2: number, y2: number, opts: { stroke: string; strokeWidth?: number; dash?: string }): string {
  return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${opts.stroke}" stroke-width="${opts.strokeWidth ?? 1}"${opts.dash ? ` stroke-dasharray="${opts.dash}"` : ''}/>`;
}

export interface HeaderSpan {
  key: string;
  label: string;
  left: number;
  width: number;
}

export function computeSpans(days: Date[], keyOf: (d: Date) => string, labelOf: (d: Date, key: string) => string): HeaderSpan[] {
  const spans: HeaderSpan[] = [];
  let startIdx = 0;
  let currentKey = '';
  days.forEach((d, i) => {
    const key = keyOf(d);
    if (key !== currentKey) {
      if (currentKey) spans.push({ key: currentKey, label: labelOf(days[startIdx], currentKey), left: startIdx * DAY_WIDTH, width: (i - startIdx) * DAY_WIDTH });
      currentKey = key;
      startIdx = i;
    }
  });
  if (currentKey) spans.push({ key: currentKey, label: labelOf(days[startIdx], currentKey), left: startIdx * DAY_WIDTH, width: (days.length - startIdx) * DAY_WIDTH });
  return spans;
}

function columnText(task: Task, col: string, dateFormat: DateFormatPattern): string {
  switch (col) {
    case 'nameFr':
      return task.nameFr;
    case 'nameEn':
      return task.nameEn;
    case 'duration':
      return task.durationDays != null ? String(task.durationDays) : '';
    case 'start':
      return formatDate(task.startDate, dateFormat);
    case 'end':
      return formatDate(task.endDate, dateFormat);
    case 'predecessors':
      return task.predecessorsText;
    case 'progress':
      return `${task.progressPct}%`;
    case 'milestone':
      return task.isClientMilestone ? '✓' : '';
    case 'diagAuto':
      return (task.diagAuto as DiagAutoType | null) ?? '';
    default:
      return '';
  }
}

export interface PageContentParams {
  tasks: Task[];
  depthById: Map<string, number>;
  layout: GlobalLayoutEntry[];
  arrows: GlobalArrow[];
  pathFor: (a: GlobalArrow) => string;
  includeLabelCol: boolean;
  columns: string[];
  labelColWidth: number;
  dateFormat: DateFormatPattern;
  weekNumbering: WeekNumbering;
  rangeStart: Date;
  dayStart: number;
  dayCount: number;
  rowStart: number;
  rowCount: number;
  /** Décalage (en jours depuis rangeStart) de la date du jour, ou null si hors du planning exporté entièrement. */
  todayDayIndex: number | null;
  weekAbbrev: (n: number) => string;
  columnLabel: (col: string) => string;
  locale: string;
}

/** Retourne le markup SVG et la taille réelle (px) du contenu de cette page (peut être plus petit que la capacité max d'une page, ex. dernière page partielle). */
export function buildPageContentSvg(p: PageContentParams): { svg: string; widthPx: number; heightPx: number } {
  const timelineWidth = p.dayCount * DAY_WIDTH;
  const contentWidth = (p.includeLabelCol ? p.labelColWidth : 0) + timelineWidth;
  const contentHeight = TIMELINE_HEADER_HEIGHT + p.rowCount * ROW_HEIGHT;
  const timelineX0 = p.includeLabelCol ? p.labelColWidth : 0;

  const parts: string[] = [];

  // --- Tableau (colonnes sélectionnées), uniquement sur la 1re colonne de pages ---
  if (p.includeLabelCol) {
    let colX = NUM_COL_WIDTH;
    parts.push(rectEl(0, 0, p.labelColWidth, TIMELINE_HEADER_HEIGHT, { fill: '#ffffff', stroke: BORDER }));
    for (const col of p.columns) {
      const w = COLUMN_WIDTH[col] ?? 70;
      parts.push(textEl(colX + 2, TIMELINE_HEADER_HEIGHT - 6, p.columnLabel(col), { fontSize: 9, fontWeight: 'bold' }));
      colX += w;
    }
    for (let i = 0; i < p.rowCount; i++) {
      const task = p.tasks[p.rowStart + i];
      if (!task) continue;
      const rowTop = TIMELINE_HEADER_HEIGHT + i * ROW_HEIGHT;
      if (task.formatting?.row?.backgroundColor) parts.push(rectEl(0, rowTop, p.labelColWidth, ROW_HEIGHT, { fill: task.formatting.row.backgroundColor }));
      parts.push(textEl(4, rowTop + ROW_HEIGHT / 2 + 4, String(task.number), { fontSize: 10, fill: TEXT_MUTED }));

      let cx = NUM_COL_WIDTH;
      for (const col of p.columns) {
        const w = COLUMN_WIDTH[col] ?? 70;
        const style = mergeCellStyle(task.formatting, col);
        if (style.backgroundColor) parts.push(rectEl(cx, rowTop, w, ROW_HEIGHT, { fill: style.backgroundColor }));
        const indent = col === p.columns[0] ? (p.depthById.get(task.id) ?? 0) * 10 : 0;
        const raw = columnText(task, col, p.dateFormat);
        const text = truncateToWidth(raw, w - 6 - indent, `${style.bold ? 'bold ' : ''}${style.fontSize ?? 11}px ${style.fontFamily || FONT_STACK}`);
        const decorations: string[] = [];
        if (style.underline) decorations.push('underline');
        if (style.strikethrough) decorations.push('line-through');
        if (style.highlightColor) parts.push(rectEl(cx + 2 + indent, rowTop + ROW_HEIGHT - 6, Math.max(w - 4 - indent, 0), 3, { fill: style.highlightColor }));
        parts.push(
          textEl(cx + 3 + indent, rowTop + ROW_HEIGHT / 2 + 4, text, {
            fontSize: style.fontSize ?? 11,
            fontWeight: style.bold ? 'bold' : undefined,
            fontStyle: style.italic ? 'italic' : undefined,
            fill: style.color ?? TEXT_DEFAULT,
            fontFamily: style.fontFamily,
            decoration: decorations.length ? decorations.join(' ') : undefined,
          }),
        );
        cx += w;
      }
      parts.push(lineEl(0, rowTop + ROW_HEIGHT, p.labelColWidth, rowTop + ROW_HEIGHT, { stroke: BORDER }));
    }
    parts.push(lineEl(p.labelColWidth, 0, p.labelColWidth, contentHeight, { stroke: BORDER, strokeWidth: 1 }));
  }

  // --- En-tête frise (mois / semaine / jour) pour la plage de jours de CETTE page ---
  const days: Date[] = [];
  for (let i = 0; i < p.dayCount; i++) days.push(addDaysUTC(p.rangeStart, p.dayStart + i));
  const monthSpans = computeSpans(
    days,
    (d) => `${d.getUTCFullYear()}-${d.getUTCMonth()}`,
    (d) => d.toLocaleDateString(p.locale, { month: 'long', year: 'numeric' }),
  );
  const weekSpans = computeSpans(
    days,
    (d) => String(weekNumber(d, p.weekNumbering)),
    (d) => p.weekAbbrev(weekNumber(d, p.weekNumbering)),
  );

  parts.push(rectEl(timelineX0, 0, timelineWidth, TIMELINE_HEADER_HEIGHT, { fill: '#ffffff', stroke: BORDER }));
  for (const span of monthSpans) {
    parts.push(lineEl(timelineX0 + span.left, 0, timelineX0 + span.left, MONTH_ROW_HEIGHT, { stroke: BORDER }));
    parts.push(textEl(timelineX0 + span.left + span.width / 2, MONTH_ROW_HEIGHT - 5, span.label, { fontSize: 10, fontWeight: 'bold', anchor: 'middle' }));
  }
  for (const span of weekSpans) {
    const y0 = MONTH_ROW_HEIGHT;
    parts.push(lineEl(timelineX0 + span.left, y0, timelineX0 + span.left, y0 + WEEK_ROW_HEIGHT, { stroke: BORDER }));
    parts.push(textEl(timelineX0 + span.left + span.width / 2, y0 + WEEK_ROW_HEIGHT - 4, span.label, { fontSize: 9, fill: TEXT_MUTED, anchor: 'middle' }));
  }
  days.forEach((d, i) => {
    const x = timelineX0 + i * DAY_WIDTH;
    const y0 = MONTH_ROW_HEIGHT + WEEK_ROW_HEIGHT;
    if (d.getUTCDay() === 1) parts.push(lineEl(x, y0, x, y0 + DAY_ROW_HEIGHT, { stroke: BORDER }));
    parts.push(textEl(x + DAY_WIDTH / 2, y0 + DAY_ROW_HEIGHT - 5, String(d.getUTCDate()), { fontSize: 9, fill: TEXT_MUTED, anchor: 'middle' }));
  });
  parts.push(lineEl(timelineX0, TIMELINE_HEADER_HEIGHT, timelineX0 + timelineWidth, TIMELINE_HEADER_HEIGHT, { stroke: BORDER }));

  // --- Grille des lignes ---
  for (let i = 0; i <= p.rowCount; i++) {
    const y = TIMELINE_HEADER_HEIGHT + i * ROW_HEIGHT;
    parts.push(lineEl(timelineX0, y, timelineX0 + timelineWidth, y, { stroke: BORDER }));
  }

  // --- Barres / jalons (écrêtés à la fenêtre de jours/lignes de cette page) ---
  const dayWindowStart = p.dayStart * DAY_WIDTH;
  const dayWindowEnd = (p.dayStart + p.dayCount) * DAY_WIDTH;
  for (let i = 0; i < p.rowCount; i++) {
    const entry = p.layout[p.rowStart + i];
    if (!entry || !entry.hasDates) continue;
    const rowTopLocal = TIMELINE_HEADER_HEIGHT + i * ROW_HEIGHT;
    const barColor = entry.task.isGroup ? GROUP_COLOR : PRIMARY;
    if (entry.isMilestone) {
      const cx = entry.barLeft + DAY_WIDTH / 2;
      if (cx < dayWindowStart - 10 || cx > dayWindowEnd + 10) continue;
      const localCx = timelineX0 + cx - dayWindowStart;
      const localCy = rowTopLocal + ROW_HEIGHT / 2;
      const half = 7;
      const points = [
        [localCx, localCy - half],
        [localCx + half, localCy],
        [localCx, localCy + half],
        [localCx - half, localCy],
      ]
        .map((pt) => pt.join(','))
        .join(' ');
      parts.push(`<polygon points="${points}" fill="${barColor}"${entry.task.isCritical ? ` stroke="${DANGER}" stroke-width="2"` : ''}/>`);
      continue;
    }
    const clampedLeft = Math.max(entry.barLeft, dayWindowStart);
    const clampedRight = Math.min(entry.barLeft + entry.barWidth, dayWindowEnd);
    const clampedWidth = clampedRight - clampedLeft;
    if (clampedWidth <= 0) continue;
    const localLeft = timelineX0 + clampedLeft - dayWindowStart;
    const barTop = rowTopLocal + ROW_HEIGHT / 2 - 8;
    parts.push(rectEl(localLeft, barTop, clampedWidth, 16, { fill: barColor, rx: 3 }));
    if (entry.task.isCritical) parts.push(rectEl(localLeft, barTop, clampedWidth, 16, { stroke: DANGER, strokeWidth: 2, rx: 3 }));
    if (!entry.task.isGroup && entry.task.progressPct > 0) {
      const progressGlobalEnd = entry.barLeft + (entry.barWidth * entry.task.progressPct) / 100;
      const visibleProgressWidth = Math.min(progressGlobalEnd, clampedRight) - clampedLeft;
      if (visibleProgressWidth > 0) parts.push(rectEl(localLeft, barTop, visibleProgressWidth, 16, { fill: 'rgba(0,0,0,0.25)' }));
    }
  }

  // --- Connecteurs (uniquement si les deux extrémités sont sur CETTE page) ---
  const rowWindowStart = p.rowStart * ROW_HEIGHT;
  const rowWindowEnd = (p.rowStart + p.rowCount) * ROW_HEIGHT;
  for (const arrow of p.arrows) {
    const within = (x: number, y: number) => x >= dayWindowStart - 20 && x <= dayWindowEnd + 20 && y >= rowWindowStart && y <= rowWindowEnd;
    if (!within(arrow.x1, arrow.y1) || !within(arrow.x2, arrow.y2)) continue;
    const shiftX = timelineX0 - dayWindowStart;
    const shiftY = TIMELINE_HEADER_HEIGHT - rowWindowStart;
    const localArrow = { ...arrow, x1: arrow.x1 + shiftX, y1: arrow.y1 + shiftY, x2: arrow.x2 + shiftX, y2: arrow.y2 + shiftY };
    const d = p.pathFor(localArrow);
    const marker = arrow.critical ? 'url(#pdf-arrow-critical)' : 'url(#pdf-arrow)';
    parts.push(`<path d="${d}" fill="none" stroke="${arrow.critical ? DANGER : TEXT_MUTED}" stroke-width="${arrow.critical ? 2 : 1.5}" marker-end="${marker}"/>`);
  }

  // --- Ligne "aujourd'hui" ---
  if (p.todayDayIndex !== null && p.todayDayIndex >= p.dayStart && p.todayDayIndex < p.dayStart + p.dayCount) {
    const x = timelineX0 + (p.todayDayIndex - p.dayStart) * DAY_WIDTH;
    parts.push(lineEl(x, 0, x, contentHeight, { stroke: TODAY_COLOR, strokeWidth: 1 }));
  }

  const defs = `<defs>
    <marker id="pdf-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="${TEXT_MUTED}"/></marker>
    <marker id="pdf-arrow-critical" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="${DANGER}"/></marker>
  </defs>`;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${contentWidth} ${contentHeight}" width="${contentWidth}" height="${contentHeight}">${defs}${parts.join('')}</svg>`;
  return { svg, widthPx: contentWidth, heightPx: contentHeight };
}
